import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import MatchCard from '../components/MatchCard';
import { MatchGridSkeleton } from '../components/MatchCardSkeleton';
import { useAuth } from '../auth/useAuth';
import { API_BASE_URL } from '../config';
import './HomePage.css';
import {
  FaBolt, FaCreditCard, FaExclamationTriangle,
  FaShieldAlt, FaCalendarAlt, FaFire, FaChevronRight,
} from 'react-icons/fa';

/* ── Date helpers ── */
function toLocal(dateStr) {
  if (!dateStr) return null;
  return new Date(dateStr.endsWith('Z') ? dateStr : dateStr + 'Z');
}
function isToday(d)    { return d && d.toDateString() === new Date().toDateString(); }
function isTomorrow(d) {
  const t = new Date(); t.setDate(t.getDate() + 1);
  return d && d.toDateString() === t.toDateString();
}

const SPORT_FILTERS = [
  // '
  // Tous', 'Football', 
  // 'Basketball', 'Tennis', 'eSports'
];

export default function HomePage({ addBet, bets }) {
  const { accessToken } = useAuth();
  const authHeaders = accessToken ? { Authorization: `Bearer ${accessToken}` } : {};

  /* ── Live fixtures ── */
  const [liveFixtures, setLiveFixtures] = useState([]);
  const [loadingLive, setLoadingLive]   = useState(true);
  const [errorLive, setErrorLive]       = useState(null);

  /* ── NS fixtures (limit 30) ── */
  const [nsFixtures, setNsFixtures]   = useState([]);
  const [loadingNs, setLoadingNs]     = useState(true);
  const [errorNs, setErrorNs]         = useState(null);

  /* ── Tabs & sport filter ── */
  const [dateTab, setDateTab]         = useState('today');    // today | tomorrow | upcoming
  const [sportFilter, setSportFilter] = useState('Tous');

  /* ── Fetch live ── */
  useEffect(() => {
    setLoadingLive(true);
    fetch(`${API_BASE_URL}/api/v1/fixtures`, {
      credentials: 'include', headers: authHeaders,
    })
      .then(r => { if (!r.ok) throw new Error(`Erreur ${r.status}`); return r.json(); })
      .then(data => {
        const all = Array.isArray(data) ? data : (data.fixtures ?? []);
        setLiveFixtures(all.filter(f => ['LIVE', '1H', '2H', 'HT'].includes(f.status)));
        setLoadingLive(false);
      })
      .catch(e => { setErrorLive(e.message); setLoadingLive(false); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  /* ── Fetch NS upcoming ── */
  useEffect(() => {
    setLoadingNs(true);
    fetch(`${API_BASE_URL}/api/v1/fixtures?status=NS&limit=30`, {
      credentials: 'include', headers: authHeaders,
    })
      .then(r => { if (!r.ok) throw new Error(`Erreur ${r.status}`); return r.json(); })
      .then(data => {
        const all = Array.isArray(data) ? data : (data.fixtures ?? []);
        setNsFixtures(all);
        setLoadingNs(false);
      })
      .catch(e => { setErrorNs(e.message); setLoadingNs(false); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  /* ── Counts per tab (for badges) ── */
  const counts = useMemo(() => {
    const bySport = (arr) => sportFilter === 'Tous'
      ? arr
      : arr.filter(m => m.sport === sportFilter);

    const todayArr    = bySport(nsFixtures.filter(m => isToday(toLocal(m.match_date))));
    const tomorrowArr = bySport(nsFixtures.filter(m => isTomorrow(toLocal(m.match_date))));
    const upcomingArr = bySport(nsFixtures.filter(m => {
      const d = toLocal(m.match_date);
      return d && !isToday(d) && !isTomorrow(d);
    }));
    return { today: todayArr, tomorrow: tomorrowArr, upcoming: upcomingArr };
  }, [nsFixtures, sportFilter]);

  /* ── Active tab matches ── */
  const activeMatches = counts[dateTab] ?? [];

  return (
    <div className="home-page">

      {/* ── WhatsApp Community Banner — top ── */}
      <a
        href="https://chat.whatsapp.com/E0RywiNhZMRIuGJt22JShB"
        target="_blank"
        rel="noreferrer"
        className="home-whatsapp-banner"
      >
        <div className="hwb-left">
          <div className="hwb-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
          </div>
          <div className="hwb-text">
            <p className="hwb-title">Rejoignez le groupe WhatsApp AFRIBET 🎉</p>
            <p className="hwb-sub">Click Ici pour rejoindre</p>
          </div>
        </div>
        <span className="hwb-cta">Rejoindre →</span>
      </a>

      {/* ── Hero ── */}
      <section className="hero">
        <div className="hero-content">
          <div className="hero-badge"><FaBolt /> Le meilleur des paris sportifs</div>
          <h1 className="hero-title">
            Parie Africain<br />
            <span className="hero-accent">Gagne Grand</span>
          </h1>
          <p className="hero-subtitle">
            Des milliers de marchés, des cotes compétitives et des retraits instantanés.
            Rejoignez plus de 180 000 parieurs actifs.
          </p>
          <div className="hero-actions">
            <Link to="/sports" className="hero-cta">Commencer à parier →</Link>
            <Link to="/live" className="hero-cta-secondary">
              <span className="live-indicator" /> Voir le Live
            </Link>
          </div>
        </div>

        {/* Scoreboard */}
        <div className="hero-visual">
          <div className="hero-scoreboard">
            <div className="scoreboard-header">🔴 EN DIRECT</div>
            {loadingLive && <div className="scoreboard-loading">⏳ Chargement...</div>}
            {!loadingLive && liveFixtures.length === 0 && (
              <div className="scoreboard-empty">Aucun match en direct</div>
            )}
            {liveFixtures.slice(0, 3).map(m => (
              <div key={m.id} className="scoreboard-row">
                <span className="sb-teams">{m.home_team} — {m.away_team}</span>
                <span className="sb-score">{m.home_score ?? 0} - {m.away_score ?? 0}</span>
                <span className="sb-time">LIVE</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Live section ── */}
      {(loadingLive || liveFixtures.length > 0) && (
        <section className="section">
          <div className="section-header">
            <h2 className="section-title">
              <span className="live-dot-anim" /> En Direct
              {!loadingLive && liveFixtures.length > 0 && (
                <span className="home-count-badge red">{liveFixtures.length}</span>
              )}
            </h2>
            <Link to="/live" className="see-all">Tout voir <FaChevronRight size={10}/></Link>
          </div>
          {loadingLive && <MatchGridSkeleton count={2} />}
          {errorLive && <div className="home-error"><FaExclamationTriangle /> {errorLive}</div>}
          {!loadingLive && liveFixtures.length > 0 && (
            <div className="matches-grid">
              {liveFixtures.slice(0, 4).map(match => (
                <MatchCard key={match.id} match={match} addBet={addBet} bets={bets} />
              ))}
            </div>
          )}
        </section>
      )}

      {/* ── Upcoming matches ── */}
      <section className="section">
        <div className="section-header">
          <h2 className="section-title">
            <FaCalendarAlt style={{ fontSize: 16 }} /> Paris Sportifs
          </h2>
          <Link to="/sports" className="see-all">Tout voir <FaChevronRight size={10}/></Link>
        </div>

        {/* ── Date tabs ── */}
        <div className="home-date-tabs">
          {[
            { key: 'today',    icon: <FaFire />,         label: "Aujourd'hui", count: counts.today.length    },
            { key: 'tomorrow', icon: <FaCalendarAlt />,  label: 'Demain',      count: counts.tomorrow.length },
            { key: 'upcoming', icon: <FaCalendarAlt />,  label: 'À venir',     count: counts.upcoming.length },
          ].map(tab => (
            <button
              key={tab.key}
              className={`home-date-tab ${dateTab === tab.key ? 'active' : ''}`}
              onClick={() => setDateTab(tab.key)}
            >
              <span className="hdt-icon">{tab.icon}</span>
              <span className="hdt-label">{tab.label}</span>
              {!loadingNs && tab.count > 0 && (
                <span className="hdt-count">{tab.count}</span>
              )}
            </button>
          ))}
        </div>

        {/* ── Sport filter pills ── */}
        <div className="sport-filters">
          {SPORT_FILTERS.map(f => (
            <button
              key={f}
              className={`filter-btn ${sportFilter === f ? 'active' : ''}`}
              onClick={() => setSportFilter(f)}
            >
              {f}
            </button>
          ))}
        </div>

        {loadingNs && <MatchGridSkeleton count={6} />}
        {errorNs   && <div className="home-error"><FaExclamationTriangle /> {errorNs}</div>}

        {!loadingNs && !errorNs && activeMatches.length === 0 && (
          <div className="home-empty">
            <span className="home-empty-icon">📅</span>
            <p>Aucun match disponible {dateTab === 'today' ? "aujourd'hui" : dateTab === 'tomorrow' ? 'demain' : 'à venir'}.</p>
          </div>
        )}

        {!loadingNs && activeMatches.length > 0 && (
          <div className="matches-grid">
            {activeMatches.slice(0, 20).map(match => (
              <MatchCard key={match.id} match={match} addBet={addBet} bets={bets} />
            ))}
          </div>
        )}
      </section>

      {/* ── Payments ── */}
      <section className="section">
        <h2 className="section-title"><FaCreditCard /> Méthodes de paiement</h2>
        <div className="payments-row">
          <Link to="/compte?section=deposit&operator=mpesa"  className="payment-badge payment-badge-link">M-Pesa</Link>
          <Link to="/compte?section=deposit&operator=orange" className="payment-badge payment-badge-link">Orange Money</Link>
          <Link to="/compte?section=deposit&operator=airtel" className="payment-badge payment-badge-link">Airtel Money</Link>
        </div>
      </section>

      <div className="responsible-gaming">
        <FaShieldAlt /> Jouez de manière responsable. AfriBet promeut le jeu responsable. 18+ uniquement.
        <a href="#help"> Aide et assistance</a>
      </div>
    </div>
  );
}
